import { Typography } from '@material-ui/core'
import React from 'react'

import { Promotion } from '../../graphql/promotions'
import { formatDiscount } from '../../helpers/promotions'
import { persianRegex } from '../../helpers/regex'
import { getTranslate, lang } from '../../localization'
import useStyles from './styles/PromotionsTable.styles'
import { usePromotionRowActions } from './usePromotionRowActions'

const dateFormatter = new Intl.DateTimeFormat(
  lang === 'fa' ? 'fa-IR-u-ca-persian' : 'en-GB',
  { dateStyle: 'medium', timeStyle: 'short' }
)

export const formatDate = (value?: string) =>
  value ? dateFormatter.format(new Date(value)) : '—'

interface PromotionRowProps {
  data: Promotion
  refetch: () => void
}

const PromotionRow: React.FC<PromotionRowProps> = ({ data, refetch }) => {
  const classes = useStyles()
  const { openRecord, actionsCell, confirmModal } = usePromotionRowActions(
    data,
    refetch
  )

  const isPersianName = persianRegex.test(data.name)

  return (
    <div>
      <div
        className={classes.tableRow}
        role="link"
        tabIndex={0}
        onClick={openRecord}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openRecord()
          }
        }}>
        <div className={classes.nameCell}>
          <Typography
            variant="h4"
            style={isPersianName ? { fontFamily: 'IranSans' } : undefined}>
            {data.name}
          </Typography>
        </div>
        {/*
          The provider's NAME, falling back to its code. `providerName` is server-resolved
          and localised by Accept-Language (npanel-bff !608); it is nullable by design, so
          the fallback is not defensive padding — it is the documented behaviour when the
          provider was removed or the lookup degraded. Never render an empty cell on the
          field that says whose margin funds the discount.

          NOT `displayProviderCode`, which is what this comment previously promised. That
          field is assigned the provider CODE at every one of its assignment sites in the
          BFF, so it would have delivered the same opaque token under a friendlier name.

          `title` keeps the full code reachable even when a name is shown — operators quote
          codes to each other in support threads, and the name alone loses that. The clip
          stays necessary either way: a long provider NAME breaks the grid track exactly as
          the code did.
        */}
        <div
          className={classes.ellipsisCell}
          title={data.providerCode}
          data-provider-code={data.providerCode}>
          {/* bdi: both a code and a Latin name are strongly-LTR inside an RTL layout. */}
          <Typography variant="body1" noWrap>
            <bdi>{data.providerName || data.providerCode}</bdi>
          </Typography>
        </div>
        <div>
          <Typography variant="body1">{getTranslate(data.status)}</Typography>
        </div>
        <div>
          <Typography variant="body1">
            {formatDiscount(
              data.discountType,
              data.discountValue,
              data.currency
            )}
          </Typography>
        </div>
        <div>
          <Typography variant="body1">{formatDate(data.updatedAt)}</Typography>
        </div>
        {actionsCell}
      </div>
      {confirmModal}
    </div>
  )
}

export default PromotionRow
